import { Link } from 'react-router-dom'

function NavBar() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-cosmic-mist/50 bg-cosmic-void/80 backdrop-blur-md">
      <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-deep to-violet-mid flex items-center justify-center shadow-glow-violet">
            <span className="text-gold text-sm">✦</span>
          </div>
          <span className="font-display text-xl font-light text-star-white tracking-wide group-hover:text-gold transition-colors">
            Shubh Time
          </span>
        </Link>
        <Link to="/login" className="text-sm text-star-soft hover:text-star-bright transition-colors">
          Sign in
        </Link>
      </div>
    </header>
  )
}

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-cosmic-void text-star-white">
      <NavBar />
      <main className="pt-24 pb-20 px-6 max-w-4xl mx-auto">
        <h1 className="font-display text-3xl font-light text-star-white mb-2">Privacy Policy</h1>
        <p className="text-star-dim text-sm font-mono mb-10">Last updated: May 2026</p>

        <div className="prose prose-invert prose-sm max-w-none space-y-8 text-star-soft leading-relaxed">

          <section>
            <h2 className="text-star-white text-lg font-semibold mb-3">1. Information We Collect</h2>
            <p>We collect information you provide directly:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 text-star-dim">
              <li><strong className="text-star-soft">Account information:</strong> name, email address, password (hashed)</li>
              <li><strong className="text-star-soft">Birth data:</strong> date, time, and place of birth for astrological calculations</li>
              <li><strong className="text-star-soft">Payment information:</strong> processed securely by Stripe — we do not store card details</li>
              <li><strong className="text-star-soft">Usage data:</strong> pages visited, features used, device and browser information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-star-white text-lg font-semibold mb-3">2. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 text-star-dim">
              <li>Provide and improve the astrological features of the Service</li>
              <li>Process payments and manage your subscription</li>
              <li>Send transactional emails (OTP codes, billing receipts)</li>
              <li>Send product updates and newsletters (you can unsubscribe at any time)</li>
              <li>Ensure security and prevent abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-star-white text-lg font-semibold mb-3">3. Birth Data and Sensitive Information</h2>
            <p>
              Your birth data (date, time, place of birth) is used solely to generate astrological
              calculations. We treat this as sensitive personal information. We do not sell, share,
              or disclose your birth data to third parties except as necessary to provide the Service
              (e.g., astronomical calculation APIs).
            </p>
          </section>

          <section>
            <h2 className="text-star-white text-lg font-semibold mb-3">4. Data Sharing</h2>
            <p>We share your information only with:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 text-star-dim">
              <li><strong className="text-star-soft">Service providers:</strong> hosting (Railway, Vercel), payments (Stripe), email (Resend) — bound by confidentiality agreements</li>
              <li><strong className="text-star-soft">AI processing:</strong> anonymized chart data may be sent to Anthropic's Claude API for interpretation — see Anthropic's privacy policy</li>
              <li><strong className="text-star-soft">Legal requirements:</strong> when required by law or to protect rights and safety</li>
            </ul>
            <p className="mt-3">We do not sell your personal data to advertisers or data brokers.</p>
          </section>

          <section>
            <h2 className="text-star-white text-lg font-semibold mb-3">5. Data Retention</h2>
            <p>
              We retain your account and birth data for as long as your account is active. You may
              request deletion of your account and associated data at any time by contacting us.
              We will delete or anonymize your data within 30 days of a verified deletion request,
              except where retention is required by law.
            </p>
          </section>

          <section>
            <h2 className="text-star-white text-lg font-semibold mb-3">6. Cookies and Analytics</h2>
            <p>
              We use Google Analytics to understand how the Service is used. Analytics data is
              aggregated and anonymized. You can opt out of Google Analytics by installing the
              Google Analytics Opt-out Browser Add-on. We use cookies for session management
              and authentication.
            </p>
          </section>

          <section>
            <h2 className="text-star-white text-lg font-semibold mb-3">7. Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 text-star-dim">
              <li>Access the personal data we hold about you</li>
              <li>Correct inaccurate personal data</li>
              <li>Request deletion of your personal data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Data portability (receive your data in a machine-readable format)</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@shubhtime.in" className="text-gold hover:text-gold/80 transition-colors">
                privacy@shubhtime.in
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-star-white text-lg font-semibold mb-3">8. Security</h2>
            <p>
              We implement industry-standard security measures including TLS encryption, hashed
              passwords, and access controls. However, no method of transmission over the internet
              is 100% secure. We encourage you to use a strong, unique password for your account.
            </p>
          </section>

          <section>
            <h2 className="text-star-white text-lg font-semibold mb-3">9. Children's Privacy</h2>
            <p>
              The Service is not directed to children under 13. We do not knowingly collect personal
              information from children under 13. If you believe we have inadvertently collected such
              information, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-star-white text-lg font-semibold mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material
              changes via email. Continued use of the Service after changes constitutes acceptance
              of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-star-white text-lg font-semibold mb-3">11. Contact</h2>
            <p>
              For privacy-related inquiries, contact us at{' '}
              <a href="mailto:privacy@shubhtime.in" className="text-gold hover:text-gold/80 transition-colors">
                privacy@shubhtime.in
              </a>
              .
            </p>
          </section>

        </div>

        <div className="mt-12 pt-8 border-t border-cosmic-mist/30 flex gap-6 text-sm">
          <Link to="/terms" className="text-gold/70 hover:text-gold transition-colors">Terms of Service</Link>
          <Link to="/" className="text-star-dim hover:text-star-soft transition-colors">← Back to home</Link>
        </div>
      </main>
    </div>
  )
}
